import { useEffect, useId, useState } from "react"
import { Trans } from "react-i18next"

import { acceptCryptoLegalDoc } from "@shared/api/accept-crypto-legal-doc/acceptCryptoLegalDoc"
import { CryptoConsentSource } from "@shared/api/accept-crypto-legal-doc/acceptCryptoLegalDoc.types"
import { UserExtraKey } from "@shared/api/extra/constants"
import myUpdateExtra from "@shared/api/extra/myUpdateExtra"
import { paybisUpdateKybTcStatus } from "@shared/api/paybis-update-kyb-tc-status/paybisUpdateKybTcStatus"
import { PaybisKybTcStatus } from "@shared/api/paybis-update-kyb-tc-status/paybisUpdateKybTcStatus.types"
import { captureException } from "@shared/lib/sentry"
import { useCryptoConsentModalStore } from "@shared/store/useCryptoConsentModalStore"
import { Button, Checkbox, Modal, ModalDescription, ModalTitle, Text } from "@shared/ui/legacy"

import {
    CRYPTO_PROVIDER_CHANGE_MODAL_CONTENT,
    DEFAULT_CRYPTO_PROVIDER_CHANGE_MODAL_LINKS,
} from "./constants"
import styles from "./cryptoProviderChangeModal.module.css"

const CryptoProviderChangeModal = () => {
    const content = CRYPTO_PROVIDER_CHANGE_MODAL_CONTENT
    const { agreements, description, header, reason, submit } = content
    const request = useCryptoConsentModalStore((state) => state.request)
    const close = useCryptoConsentModalStore((state) => state.close)
    const [isBanxeAgreementAccepted, setIsBanxeAgreementAccepted] = useState(false)
    const [isPaybisAgreementAccepted, setIsPaybisAgreementAccepted] = useState(false)
    const [isReasonOpen, setIsReasonOpen] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSkipping, setIsSkipping] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const reasonContentId = useId()
    const links = DEFAULT_CRYPTO_PROVIDER_CHANGE_MODAL_LINKS
    const isSubmitDisabled = !isBanxeAgreementAccepted || !isPaybisAgreementAccepted || isSubmitting || isSkipping

    useEffect(() => {
        if (request) {
            setIsBanxeAgreementAccepted(false)
            setIsPaybisAgreementAccepted(false)
            setIsReasonOpen(true)
            setError(null)
        }
    }, [request])

    const handleAgree = async () => {
        if (!request) {
            return
        }

        setIsSubmitting(true)
        setError(null)
        try {
            await acceptCryptoLegalDoc({
                accessToken: request.accessToken,
                companyId: request.companyId,
                cryptoProviderTermsId: request.cryptoProviderTermsId,
                source: CryptoConsentSource.LOGIN_POPUP,
            })
            if (request.companyId) {
                await paybisUpdateKybTcStatus({
                    accessToken: request.accessToken,
                    companyId: request.companyId,
                    tcStatus: PaybisKybTcStatus.ACCEPTED,
                })
            }
            close()
            request.onAccepted()
        } catch (submitError: unknown) {
            setError(
                submitError instanceof Error
                    ? submitError.message
                    : submit.error
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleRemindLater = async () => {
        if (!request) {
            return
        }

        setIsSkipping(true)
        try {
            await myUpdateExtra(
                {
                    key: UserExtraKey.PAYBIS_POPUP_SKIP_COUNTER,
                    data: request.skipCounter + 1,
                },
                request.accessToken
            )
        } catch (skipError: unknown) {
            captureException(skipError)
        } finally {
            setIsSkipping(false)
            close()
            request.onAccepted()
        }
    }

    return (
        <Modal
            contentClassName={ styles.modal }
            contentProps={{
                onEscapeKeyDown: (event) => event.preventDefault(),
                onInteractOutside: (event) => event.preventDefault(),
                onPointerDownOutside: (event) => event.preventDefault(),
            }}
            open={ Boolean(request) }
            onOpenChange={ () => undefined }
        >
            <header className={ styles.header }>
                <span className={ styles.changeIcon } aria-hidden="true">
                    { header.icon }
                </span>
                <ModalTitle asChild>
                    <Text as="h2" variant="title">
                        { header.title }
                    </Text>
                </ModalTitle>
            </header>
            <div className={ styles.main }>
                <ModalDescription asChild>
                    <div className={ styles.description }>
                        <Text>{ description.intro }</Text>
                        <Text>
                            <Trans
                                i18nKey="crypto_provider_change_modal.pro_wallet_description"
                                defaults={ description.proWallet }
                                components={{
                                    proWallet: <a className={ styles.link } href={ links.proWallet } />,
                                }}
                            />
                        </Text>
                    </div>
                </ModalDescription>

                <section>
                    <button
                        aria-controls={ reasonContentId }
                        aria-expanded={ isReasonOpen }
                        className={ styles.reasonSummary }
                        type="button"
                        onClick={ () => setIsReasonOpen((isOpen) => !isOpen) }
                    >
                        <span className={ styles.reasonChevron } aria-hidden="true" />
                        <Text as="span" variant="link">
                            { reason.title }
                        </Text>
                    </button>
                    <div className={ isReasonOpen
                        ? `${ styles.collapsibleContent } ${ styles.collapsibleContentOpen }`
                        : styles.collapsibleContent }
                    >
                        <div className={ styles.collapsibleContentInner } id={ reasonContentId }>
                            <Text variant="bodyMuted">
                                { reason.paybisMica }
                            </Text>
                        </div>
                    </div>

                    <div className={ styles.reasonNotice }>
                        <span className={ styles.reasonNoticeIcon } aria-hidden="true">
                            { reason.infoIcon }
                        </span>
                        <Text variant="bodyMuted">
                            <Trans
                                i18nKey="crypto_provider_change_modal.privacy_policy_description"
                                defaults={ reason.privacyPolicyDescription }
                                components={{
                                    banxePrivacyPolicy: (
                                        <a
                                            className={ styles.linkMuted }
                                            href={ links.banxePrivacyPolicy }
                                        />
                                    ),
                                    paybisPrivacyPolicy: (
                                        <a
                                            className={ styles.linkMuted }
                                            href={ links.paybisPrivacyPolicy }
                                        />
                                    ),
                                }}
                            />
                        </Text>
                    </div>
                </section>
            </div>

            <footer className={ styles.agreements }>
                <Text as="h3" variant="sectionTitle">
                    { agreements.title }
                </Text>
                <Text className={ styles.agreementHint }>
                    { agreements.hint }
                </Text>

                <div className={ styles.checkboxList }>
                    <Checkbox
                        checked={ isBanxeAgreementAccepted }
                        label={
                            <Trans
                                i18nKey="crypto_provider_change_modal.banxe_agreement"
                                defaults={ agreements.banxe }
                                components={{
                                    privacyPolicy: <a href={ links.banxePrivacyPolicy } />,
                                    terms: <a href={ links.banxeTerms } />,
                                }}
                            />
                        }
                        onChange={ (event) => setIsBanxeAgreementAccepted(event.target.checked) }
                    />
                    <Checkbox
                        checked={ isPaybisAgreementAccepted }
                        label={
                            <Trans
                                i18nKey="crypto_provider_change_modal.paybis_agreement"
                                defaults={ agreements.paybis }
                                components={{
                                    privacyPolicy: <a href={ links.paybisPrivacyPolicy } />,
                                    terms: <a href={ links.paybisTerms } />,
                                }}
                            />
                        }
                        onChange={ (event) => setIsPaybisAgreementAccepted(event.target.checked) }
                    />
                </div>

                { error ? <Text className={ styles.submitError }>{ error }</Text> : null }

                <Button
                    className={ styles.agreeButton }
                    disabled={ isSubmitDisabled }
                    fullWidth
                    onClick={ () => void handleAgree() }
                >
                    { isSubmitting
                        ? submit.pending
                        : submit.idle }
                </Button>

                <button
                    className={ styles.remindLaterButton }
                    disabled={ isSubmitting || isSkipping }
                    type="button"
                    onClick={ () => void handleRemindLater() }
                >
                    <Text as="span" variant="link">
                        { submit.remindLater }
                    </Text>
                </button>
            </footer>
        </Modal>
    )
}

export default CryptoProviderChangeModal
